import React from 'react';
import Header from './Header';
import Footer from './Footer';
import { ArrowRight, Check, Shield, Mail, Users, Cloud, Inbox, Star } from 'lucide-react';

const EMAIL_PLANS = [
  {
    title: 'Email Pro Start',
    price: '7,90',
    oldPrice: 'R$19,90/mês',
    savings: 'ECONOMIZE 60%',
    features: [
      { text: '5 GB de armazenamento por caixa', info: 'Espaço suficiente para e-mails, anexos e histórico.' },
      { text: '1 conta de email profissional', info: 'Exemplo: contato@seudominio.com' },
      { text: 'Webmail moderno e responsivo', info: 'Acesse seus e-mails de qualquer lugar, em qualquer dispositivo.' },
      { text: 'Antivírus e Antispam avançados', info: 'Proteção automática contra ameaças e lixo eletrônico.' },
      { text: 'Suporte IMAP/POP/SMTP', info: 'Compatível com Outlook, Gmail, Thunderbird e outros.' },
      { text: 'Suporte técnico 24/7', info: 'Ajuda especializada sempre disponível.' },
    ],
    subtitle: 'Para quem está começando com email profissional.'
  },
  {
    title: 'Email Pro Plus',
    price: '14,90',
    oldPrice: 'R$34,90/mês',
    savings: 'ECONOMIZE 57%',
    features: [
      { text: '30 GB de armazenamento por caixa', info: 'Muito espaço para e-mails, anexos e histórico.' },
      { text: '10 contas de email profissional', info: 'Exemplo: equipe@seudominio.com' },
      { text: 'Webmail moderno e responsivo', info: 'Acesse seus e-mails de qualquer lugar, em qualquer dispositivo.' },
      { text: 'Antivírus e Antispam avançados', info: 'Proteção automática contra ameaças e lixo eletrônico.' },
      { text: 'Suporte IMAP/POP/SMTP', info: 'Compatível com Outlook, Gmail, Thunderbird e outros.' },
      { text: 'Migração gratuita de contas', info: 'Trazemos seus e-mails de outro provedor sem custo.' },
      { text: 'Suporte técnico 24/7', info: 'Ajuda especializada sempre disponível.' },
    ],
    subtitle: 'Para equipes e empresas que precisam de mais.'
  }
];

const BENEFITS = [
  {
    icon: <Mail size={28} className="text-purple-400" />,
    title: 'Profissionalize sua comunicação',
    desc: 'Transmita mais confiança e credibilidade com e-mails personalizados no seu domínio.'
  },
  {
    icon: <Shield size={28} className="text-purple-400" />,
    title: 'Segurança de ponta',
    desc: 'Antivírus, antispam e proteção avançada para manter seus dados e mensagens sempre seguros.'
  },
  {
    icon: <Inbox size={28} className="text-purple-400" />,
    title: 'Acesso em qualquer lugar',
    desc: 'Webmail moderno, integração com Outlook, Gmail, apps mobile e sincronização total.'
  },
  {
    icon: <Cloud size={28} className="text-purple-400" />,
    title: 'Infraestrutura Cloud',
    desc: 'Servidores de alta disponibilidade, backups automáticos e performance garantida.'
  },
  {
    icon: <Users size={28} className="text-purple-400" />,
    title: 'Para você ou sua equipe',
    desc: 'Planos sob medida para profissionais autônomos, pequenas empresas e grandes equipes.'
  },
  {
    icon: <Star size={28} className="text-purple-400" />,
    title: 'Suporte 24/7',
    desc: 'Conte com atendimento especializado sempre que precisar, por chat, e-mail ou telefone.'
  },
];

function EmailPlanCard({ plan, recommended }) {
  return (
    <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-gray-900 via-gray-950 to-gray-900 border border-purple-900 shadow-xl flex flex-col">
      <div className="h-2 w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-700" />
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <h3 className="text-lg md:text-xl font-bold text-white mb-1 tracking-tight drop-shadow-sm">{plan.title}</h3>
        <div className="text-xs md:text-sm text-purple-200 font-medium mb-3 mt-2">{plan.subtitle}</div>
        <div className="flex flex-col items-start mb-4 mt-2 relative h-auto w-full">
          <div className="flex items-center mb-1">
            <span className="text-base md:text-lg text-gray-400 line-through mr-2 select-none">{plan.oldPrice}</span>
            <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-500 text-white text-xs font-bold shadow-sm border border-purple-400/40 tracking-wide uppercase" style={{letterSpacing: '0.04em'}}>{plan.savings}</span>
          </div>
          <span className="text-3xl md:text-4xl font-bold text-purple-300 leading-tight flex items-baseline">
            <span className="text-base md:text-lg text-purple-200 mr-0.5">R$</span>{plan.price}<span className="text-xs md:text-sm text-gray-400 ml-0.5 font-normal">/mês</span>
          </span>
          <span className="text-[11px] md:text-xs text-gray-400 font-normal mt-1 mb-2 block">Preço promocional para 12 meses.</span>
        </div>
        <button
          className={
            recommended
              ? 'w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm md:text-base flex justify-center items-center mb-0 mt-1'
              : 'w-full py-3 px-4 rounded-xl font-semibold text-purple-400 border-2 border-purple-600 transition-all duration-300 bg-transparent hover:bg-purple-900/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm md:text-base flex justify-center items-center mb-0 mt-1'
          }
        >
          Quero este plano <ArrowRight size={15} className="ml-2" />
        </button>
        <hr className="border-t border-gray-700/60 my-4 mb-5 w-full" />
        <ul className="space-y-2 mb-5 flex-1">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start text-[15px] font-medium text-gray-100">
              <div className="p-1 rounded-full mt-0.5 mr-2 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-purple-800/80 to-indigo-900/80"><Check size={13} className="text-purple-300" /></div>
              <span>{f.text} {f.info && <span className="ml-1 text-purple-400" title={f.info}>?</span>}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function EmailProfissionalLandingPage() {
  return (
    <div className="bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 min-h-screen flex flex-col">
      <Header />
      {/* HERO */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 text-center relative z-10">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900/40 via-transparent to-indigo-900/30 pointer-events-none" />
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">Email Profissional Corporativo</h1>
        <p className="text-lg md:text-2xl text-gray-300 max-w-2xl mx-auto mb-8">Tenha credibilidade, segurança e produtividade com e-mails personalizados para sua empresa.<br className="hidden md:block" />
        Solução robusta, moderna e fácil de usar para profissionais e equipes de todos os tamanhos.</p>
        <div className="flex flex-col md:flex-row gap-4 justify-center items-center mt-8">
          <a href="#planos-email" className="px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 text-lg transition-all">Ver Planos</a>
          <a href="#beneficios-email" className="px-8 py-4 rounded-xl font-bold text-purple-400 border-2 border-purple-600 bg-transparent hover:bg-purple-900/10 hover:text-white transition-all text-lg">Por que escolher?</a>
        </div>
      </section>
      {/* BENEFÍCIOS */}
      <section id="beneficios-email" className="py-12 md:py-20">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-10">Por que escolher nosso Email Profissional?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-gray-900/80 rounded-2xl p-6 flex flex-col items-center text-center shadow-lg border border-purple-900">
                <div className="mb-3">{b.icon}</div>
                <h3 className="text-lg font-bold text-purple-200 mb-2">{b.title}</h3>
                <p className="text-gray-300 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* PLANOS */}
      <section id="planos-email" className="py-12 md:py-20 bg-gradient-to-b from-gray-950/80 to-transparent">
        <div className="container mx-auto px-2 md:px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div></div>
            <div className="flex justify-center -mb-6">
              <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold shadow-md tracking-wide uppercase flash-effect">Recomendado</span>
              <style>{`
                .flash-effect {
                  position: relative;
                  overflow: hidden;
                }
                .flash-effect::after {
                  content: '';
                  position: absolute;
                  top: 0; left: -70%;
                  width: 80%; height: 100%;
                  background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 30%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.18) 70%, rgba(255,255,255,0) 100%);
                  filter: blur(2px);
                  transform: skewX(-18deg);
                  animation: flash-move 2.5s cubic-bezier(0.4,0,0.2,1) infinite;
                  pointer-events: none;
                }
                @keyframes flash-move {
                  0% { left: -70%; }
                  100% { left: 110%; }
                }
              `}</style>
            </div>
            <EmailPlanCard plan={EMAIL_PLANS[0]} />
            <EmailPlanCard plan={EMAIL_PLANS[1]} recommended />
          </div>
        </div>
      </section>
      {/* CTA FINAL */}
      <section className="py-12 md:py-20 text-center">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Pronto para profissionalizar seu email?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">Garanta já seu endereço personalizado, aumente a confiança dos seus clientes e tenha suporte de verdade sempre que precisar.</p>
          <a href="#planos-email" className="inline-block px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 text-lg transition-all">Quero meu Email Profissional</a>
        </div>
      </section>
      <Footer />
    </div>
  );
} 